import { memo } from "react";
import { styled, Typography } from "@schaeffler-commerce/design-system";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import { C_CHART_8, C_SURFACE_CONTAINER_HIGHEST } from "@schaeffler-commerce/design-system";
import { convertToRoman } from "~/common/utils/convertToRomanNumeral";
import {
  TargetGraphTooltip,
  TargetGraphTooltipContent,
  TargetGraphTooltipEntry,
} from "~/features/cms/homepagePersonalization/cpd/components/bonusAgreementTile/components/targetGraph/TargetGraphTooltip";
import { makeFormatCurrency } from "~/features/cms/homepagePersonalization/cpd/components/bonusAgreementTile/bonusAgeementUtil";

export interface TargetCircleProps {
  /**
   * Index of the circle (0-based)
   */
  index: number;
  /**
   * Target value for this circle
   */
  targetValue: number;
  /**
   * Current invoice value
   */
  invoiceValue: number;
  /**
   * Position as percentage (0-100) along the progress bar
   */
  position: number;
  /**
   * Cashback rate for this target (0-1 scale)
   */
  cashbackRate?: number;
  /**
   * Gap to reach this target
   */
  gapToTarget?: number;

  currency: string;
}

const Circle = styled("div")<{ $isFilled: boolean; $position: number }>(({ $isFilled, $position }) => ({
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  transition: "background-color 0.3s ease-in-out",
  boxSizing: "border-box",
  padding: "2px 0",
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
  pointerEvents: "auto",
  backgroundColor: $isFilled ? "#c9dea5" : C_SURFACE_CONTAINER_HIGHEST,
  left: $position === 100 ? `calc(${$position}% - 24px)` : `${$position}%`,
  cursor: "pointer",
}));

const TargetCircle = ({
  index,
  targetValue,
  invoiceValue,
  position,
  cashbackRate,
  gapToTarget,
  currency,
}: TargetCircleProps) => {
  const isFilled = invoiceValue >= targetValue;

  const formatCurrency = makeFormatCurrency(currency);

  const tooltipContent = (
    <TargetGraphTooltipContent sx={{ gap: "4px" }}>
      <TargetGraphTooltipEntry
        isFilled={isFilled}
        label={`Target ${convertToRoman(index + 1)}`}
        value={formatCurrency(targetValue)}
        color={C_CHART_8}
      />
      {!isFilled && (
        <TargetGraphTooltipEntry
          isFilled={isFilled}
          label="Gap to target"
          value={gapToTarget !== undefined ? formatCurrency(gapToTarget) : "—"}
        />
      )}
      <TargetGraphTooltipEntry
        isFilled={isFilled}
        label="Cashback rate"
        value={cashbackRate !== undefined ? `${(cashbackRate * 100).toFixed(0)}%` : "—"}
      />
    </TargetGraphTooltipContent>
  );

  return (
    <TargetGraphTooltip title={tooltipContent}>
      <Circle $isFilled={isFilled} $position={position}>
        {isFilled ? (
          <CheckCircleOutlineIcon color="primary" />
        ) : (
          <Typography variant="titleMedium" sx={{
            fontWeight: 500
          }}>
            {"€".repeat(index + 1)}
          </Typography>
        )}
      </Circle>
    </TargetGraphTooltip>
  );
};

export default memo(TargetCircle);
